import React from 'react';

// Parse mentions from text and return structured data
export const parseMentions = (text) => {
  const mentionRegex = /@(\w+)/g;
  const mentions = [];
  let match;
  
  while ((match = mentionRegex.exec(text)) !== null) {
    mentions.push({
      mention: match[0], // @username
      username: match[1], // username
      start: match.index,
      end: match.index + match[0].length
    });
  }
  
  return mentions;
};

// Extract mention usernames/department names for backend processing
export const extractMentionNames = (text) => {
  const mentions = parseMentions(text);
  return mentions.map(mention => mention.username);
};

// URL regex pattern to match various URL formats - simplified and more reliable
const URL_REGEX = /(https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9-]+\.[a-zA-Z]{2,}[^\s]*)/gi;

// Helper function to ensure URL has protocol
const ensureProtocol = (url) => {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `https://${url}`;
};

// Render text with highlighted mentions as HTML string (for dangerouslySetInnerHTML)
export const renderMentionsAsHTML = (text) => {
  if (!text) return '';
  
  // Split text into lines
  const lines = text.split('\n');
  
  // Process each line for mentions and URLs
  const processedLines = lines.map(line => {
    if (!line.trim()) return '&nbsp;'; // Non-breaking space for empty lines
    
    let result = line;
    
    // First, handle URLs
    result = result.replace(URL_REGEX, (match) => {
      const href = ensureProtocol(match);
      return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">${match}</a>`;
    });
    
    // Then handle mentions
    const mentionRegex = /@(\w+)/g;
    result = result.replace(mentionRegex, (match) => {
      return `<span class="bg-indigo-100 text-indigo-800 px-1 py-0.5 rounded text-sm font-medium">${match}</span>`;
    });
    
    return result;
  });
  
  // If single line, return without paragraph wrapper
  if (lines.length === 1) {
    return processedLines[0];
  }
  
  // Multiple lines - wrap each in paragraph tags
  return processedLines
    .map(line => `<p class="mb-2 last:mb-0">${line}</p>`)
    .join('');
};

// Parse text into segments (mentions, URLs, and regular text)
const parseTextSegments = (text) => {
  if (!text) return [];
  
  const segments = [];
  const mentionRegex = /@(\w+)/g;
  
  // Find all mentions and URLs with their positions
  const items = [];
  
  // Find mentions
  let match;
  while ((match = mentionRegex.exec(text)) !== null) {
    items.push({
      type: 'mention',
      content: match[0],
      start: match.index,
      end: match.index + match[0].length
    });
  }
  
  // Find URLs
  const urlMatches = [...text.matchAll(URL_REGEX)];
  urlMatches.forEach(match => {
    items.push({
      type: 'url',
      content: match[0],
      start: match.index,
      end: match.index + match[0].length
    });
  });
  
  // Sort by position
  items.sort((a, b) => a.start - b.start);
  
  // Build segments
  let lastIndex = 0;
  
  items.forEach((item, index) => {
    // Add text before this item
    if (item.start > lastIndex) {
      segments.push({
        type: 'text',
        content: text.substring(lastIndex, item.start)
      });
    }
    
    // Add the item
    segments.push(item);
    lastIndex = item.end;
  });
  
  // Add remaining text
  if (lastIndex < text.length) {
    segments.push({
      type: 'text',
      content: text.substring(lastIndex)
    });
  }
  
  return segments;
};

// Render a single line with mentions and URLs
const renderLineWithMentions = (line, lineIndex) => {
  if (!line) return '\u00A0'; // Non-breaking space for empty lines
  
  const segments = parseTextSegments(line);
  
  // If no special segments found, return plain text
  if (segments.length === 0) {
    return line;
  }
  
  // If only one segment and it's plain text, return it as-is
  if (segments.length === 1 && segments[0].type === 'text') {
    return line;
  }
  
  return segments.map((segment, index) => {
    switch (segment.type) {
      case 'mention':
        return (
          <span 
            key={`mention-${lineIndex}-${index}`}
            className="bg-indigo-100 text-indigo-800 px-1 py-0.5 rounded text-sm font-medium"
          >
            {segment.content}
          </span>
        );
      case 'url':
        const href = ensureProtocol(segment.content);
        return (
          <a
            key={`url-${lineIndex}-${index}`}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 underline"
          >
            {segment.content}
          </a>
        );
      default:
        return segment.content;
    }
  });
};

// Render text with highlighted mentions and clickable URLs
export const renderTextWithMentions = (text, className = '') => {
  if (!text) return '';
  
  // Split text into lines
  const lines = text.split('\n');
  
  // If it's just one line, render it inline without paragraph wrapper
  if (lines.length === 1) {
    const segments = parseTextSegments(text);
    
    // If no special segments found, return plain text
    if (segments.length === 0) {
      return <span className={className}>{text}</span>;
    }
    
    // If only one segment and it's plain text, return it as-is
    if (segments.length === 1 && segments[0].type === 'text') {
      return <span className={className}>{text}</span>;
    }
    
    const renderedSegments = segments.map((segment, index) => {
      switch (segment.type) {
        case 'mention':
          return (
            <span 
              key={`mention-${index}`}
              className="bg-indigo-100 text-indigo-800 px-1 py-0.5 rounded text-sm font-medium"
            >
              {segment.content}
            </span>
          );
        case 'url':
          const href = ensureProtocol(segment.content);
          return (
            <a
              key={`url-${index}`}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline"
            >
              {segment.content}
            </a>
          );
        default:
          return segment.content;
      }
    });
    
    return <span className={className}>{renderedSegments}</span>;
  }
  
  // Multiple lines - render as paragraphs
  return (
    <div className={className}>
      {lines.map((line, index) => (
        <p key={index} className="mb-2 last:mb-0">
          {renderLineWithMentions(line, index)}
        </p>
      ))}
    </div>
  );
};

// Validate if a mention exists in the provided users/departments
export const validateMentions = (text, users = [], departments = []) => {
  const mentionNames = extractMentionNames(text);
  const validMentions = [];
  const invalidMentions = [];
  
  mentionNames.forEach(mentionName => {
    const normalizedMention = mentionName.toLowerCase();
    
    // Check if it's a valid user
    const user = users.find(u => 
      u.name.replace(/\s+/g, '').toLowerCase() === normalizedMention
    );
    
    // Check if it's a valid department
    const department = departments.find(d => 
      d.name.replace(/\s+/g, '').toLowerCase() === normalizedMention
    );
    
    if (user) {
      validMentions.push({
        type: 'user',
        id: user.id,
        name: user.name,
        mention: `@${mentionName}`
      });
    } else if (department) {
      validMentions.push({
        type: 'department',
        id: department.id,
        name: department.name,
        mention: `@${mentionName}`
      });
    } else {
      invalidMentions.push(`@${mentionName}`);
    }
  });
  
  return {
    valid: validMentions,
    invalid: invalidMentions,
    isValid: invalidMentions.length === 0
  };
};

// NEW: Render rich text HTML content with mentions properly formatted
export const renderRichTextWithMentions = (htmlContent, className = '') => {
  if (!htmlContent) return '';
  
  // For React, we'll use dangerouslySetInnerHTML but process mentions first
  let processedHTML = htmlContent;
  
  // Process mentions in HTML content - but avoid double-processing already styled mentions
  const mentionRegex = /@(\w+)(?![^<]*<\/span>)/g;
  processedHTML = processedHTML.replace(mentionRegex, (match) => {
    return `<span class="bg-indigo-100 text-indigo-800 px-1 py-0.5 rounded text-sm font-medium">${match}</span>`;
  });
  
  // Add proper CSS classes to HTML elements for better styling
  processedHTML = processedHTML
    // Style unordered lists
    .replace(/<ul>/g, '<ul class="list-disc list-inside mb-3 space-y-1">')
    // Style ordered lists  
    .replace(/<ol>/g, '<ol class="list-decimal list-inside mb-3 space-y-1">')
    // Style list items
    .replace(/<li>/g, '<li class="text-gray-700">')
    // Style paragraphs
    .replace(/<p>/g, '<p class="mb-2 last:mb-0">')
    // Style headings
    .replace(/<h1>/g, '<h1 class="text-2xl font-bold mb-2 text-gray-900">')
    .replace(/<h2>/g, '<h2 class="text-xl font-semibold mb-2 text-gray-900">')
    .replace(/<h3>/g, '<h3 class="text-lg font-medium mb-2 text-gray-900">')
    // Style blockquotes
    .replace(/<blockquote>/g, '<blockquote class="border-l-4 border-gray-300 pl-4 py-2 mb-3 italic text-gray-600 bg-gray-50 rounded-r">')
    // Style code blocks
    .replace(/<pre>/g, '<pre class="bg-gray-900 text-gray-100 p-3 rounded mb-3 overflow-x-auto text-sm">')
    // Style inline code
    .replace(/<code>/g, '<code class="bg-gray-100 text-red-600 px-1 py-0.5 rounded text-sm font-mono">')
    // Style links
    .replace(/<a /g, '<a class="text-blue-600 hover:text-blue-800 underline" ')
    // Style strong/bold
    .replace(/<strong>/g, '<strong class="font-semibold">')
    // Style emphasis/italic
    .replace(/<em>/g, '<em class="italic">');
  
  return (
    <div 
      className={`rich-text-content ${className}`}
      dangerouslySetInnerHTML={{ __html: processedHTML }}
    />
  );
};

// NEW: Extract plain text from HTML content for character counting
export const extractTextFromHTML = (htmlContent) => {
  if (!htmlContent) return '';
  
  // Remove HTML tags and get plain text
  if (typeof document !== 'undefined') {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    return tempDiv.textContent || tempDiv.innerText || '';
  } else {
    // Fallback for server-side rendering
    return htmlContent.replace(/<[^>]*>/g, '');
  }
};

// NEW: Check if content is HTML or plain text
export const isHTMLContent = (content) => {
  if (!content) return false;
  return /<[^>]*>/g.test(content);
};

// NEW: Smart render function that handles both HTML and plain text
export const renderContentWithMentions = (content, className = '') => {
  if (!content) return '';
  
  // Check if content is HTML
  if (isHTMLContent(content)) {
    return renderRichTextWithMentions(content, className);
  } else {
    return renderTextWithMentions(content, className);
  }
};

// NEW: Process mentions in rich text editor content
export const processMentionsInRichText = (htmlContent) => {
  if (!htmlContent) return htmlContent;
  
  // Replace @mentions with styled spans, but avoid double-processing
  const mentionRegex = /@(\w+)(?![^<]*<\/span>)/g;
  return htmlContent.replace(mentionRegex, (match) => {
    return `<span class="bg-indigo-100 text-indigo-800 px-1 py-0.5 rounded text-sm font-medium">${match}</span>`;
  });
};

// NEW: Convert HTML mentions back to plain text format (for editing)
export const convertHTMLMentionsToPlainText = (htmlContent) => {
  if (!htmlContent) return '';
  
  // Replace styled mention spans with plain @mentions
  const styledMentionRegex = /<span[^>]*class="[^"]*bg-indigo-100[^"]*"[^>]*>(@\w+)<\/span>/g;
  let plainText = htmlContent.replace(styledMentionRegex, '$1');
  
  // Remove other HTML tags
  plainText = plainText.replace(/<[^>]*>/g, '');
  
  return plainText;
};

// NEW: Extract mentions from HTML content
export const extractMentionsFromHTML = (htmlContent) => {
  if (!htmlContent) return [];
  
  const mentions = [];
  
  // Extract from styled spans
  const styledMentionRegex = /<span[^>]*class="[^"]*bg-indigo-100[^"]*"[^>]*>@(\w+)<\/span>/g;
  let match;
  while ((match = styledMentionRegex.exec(htmlContent)) !== null) {
    mentions.push(match[1]);
  }
  
  // Extract plain mentions not in spans
  const plainText = extractTextFromHTML(htmlContent);
  const plainMentions = extractMentionNames(plainText);
  
  // Combine and deduplicate
  const allMentions = [...new Set([...mentions, ...plainMentions])];
  return allMentions;
};

// NEW: Validate mentions in HTML content
export const validateHTMLMentions = (htmlContent, users = [], departments = []) => {
  const mentionNames = extractMentionsFromHTML(htmlContent);
  const validMentions = [];
  const invalidMentions = [];
  
  mentionNames.forEach(mentionName => {
    const normalizedMention = mentionName.toLowerCase();
    
    // Check if it's a valid user
    const user = users.find(u => 
      u.name.replace(/\s+/g, '').toLowerCase() === normalizedMention
    );
    
    // Check if it's a valid department
    const department = departments.find(d => 
      d.name.replace(/\s+/g, '').toLowerCase() === normalizedMention
    );
    
    if (user) {
      validMentions.push({
        type: 'user',
        id: user.id,
        name: user.name,
        mention: `@${mentionName}`
      });
    } else if (department) {
      validMentions.push({
        type: 'department',
        id: department.id,
        name: department.name,
        mention: `@${mentionName}`
      });
    } else {
      invalidMentions.push(`@${mentionName}`);
    }
  });
  
  return {
    valid: validMentions,
    invalid: invalidMentions,
    isValid: invalidMentions.length === 0
  };
}; 